import { availableEnergy, effectiveCardCost } from '../cards/query.js';
import { canonicalCellOrder } from '../state/board.js';
import { legacyPlanCardSelections, placementCellFromSelections, planSelections, revalidatePlannedTargets, targetInstanceFromSelections, unitInstanceFromStep, validateCommittedPlanningSelections, } from '../targeting/planning.js';
const accepted = () => ({ ok: true });
const rejected = (reason) => ({ ok: false, reason });
export function isFieldPlan(plan) {
    return plan.kind === undefined || plan.kind === 'FIELD';
}
function planningGate(state, player) {
    if (state.phase !== 'planning')
        return rejected('действие доступно только в фазе planning');
    if (player.ready)
        return rejected(`игрок "${player.id}" уже подтвердил готовность`);
    return null;
}
export function committedPlanCost(state, player, registry) {
    let total = 0;
    for (const plan of player.staged) {
        const cost = effectiveCardCost(state, registry, plan.instanceId);
        if (cost === undefined)
            return undefined;
        total += cost;
    }
    return total;
}
function validateHandCard(state, actor, instanceId, registry) {
    const instance = state.instances[instanceId];
    if (instance === undefined || instance.ownerId !== actor.id || instance.zone !== 'hand'
        || !actor.hand.includes(instance.id))
        return rejected('карта должна находиться в руке действующего игрока');
    if (Object.values(state.players).some((player) => player.staged.some((plan) => plan.instanceId === instanceId))) {
        return rejected(`карта "${instanceId}" уже добавлена в план`);
    }
    const definition = registry.get(instance.defId);
    if (definition === undefined)
        return rejected(`определение карты "${instance.defId}" отсутствует в registry`);
    return { definition };
}
function validateFieldDestination(state, actor, cellId, definition, selections, registry) {
    if (cellId === undefined)
        return rejected('UNIT/STRUCTURE требует cellId');
    const cell = state.board.cells[cellId];
    if (!canonicalCellOrder.includes(cellId) || cell === undefined || cell.id !== cellId) {
        return rejected(`неизвестная или неканоническая ячейка "${cellId}"`);
    }
    if (cell.side !== actor.side)
        return rejected('можно планировать выкладку только на свою сторону');
    const placement = definition.planning?.fieldPlacement;
    const overlayHostId = typeof placement === 'object'
        ? unitInstanceFromStep(selections, placement.hostStepId)
        : undefined;
    if (typeof placement === 'object') {
        const host = overlayHostId === undefined ? undefined : state.instances[overlayHostId];
        if (host === undefined || host.zone !== 'board' || host.cellId !== cell.id
            || host.controllerId !== actor.id || cell.occupantId !== host.id) {
            return rejected(`Наложение требует точного подконтрольного occupant ячейки "${cellId}"`);
        }
        if (state.captureReservations.some((reservation) => reservation.sourceCellId === cellId)) {
            return rejected(`ячейка "${cellId}" зарезервирована временным Захватом`);
        }
        if (Object.values(state.players).some((player) => player.staged.some((plan) => isFieldPlan(plan) && plan.cellId === cellId))) {
            return rejected(`Наложение требует незарезервированный staged-слот ячейки "${cellId}"`);
        }
    }
    else if (Object.values(state.players).some((player) => player.staged.some((plan) => {
        if (!isFieldPlan(plan) || plan.cellId !== cellId)
            return false;
        const plannedInstance = state.instances[plan.instanceId];
        const plannedDefinition = plannedInstance === undefined
            ? undefined
            : registry.get(plannedInstance.defId);
        return typeof plannedDefinition?.planning?.fieldPlacement === 'object';
    }))) {
        return rejected(`обычный FIELD не может делить staged-слот с Наложением в ячейке "${cellId}"`);
    }
    // Ordinary FIELD placement is a committed intent, not a reservation. An
    // occupied/capture-reserved/already-planned cell is legal to queue; the exact
    // queue position authoritatively revalidates it against the live board.
    return accepted();
}
/** Повторная проверка цели SPELL на текущем authoritative draft перед reveal. */
export function spellTargetIsValid(state, definition, plan, playerId, registry) {
    if (registry === undefined)
        return false;
    try {
        if (plan.kind === 'PlanCard') {
            const selections = legacyPlanCardSelections(plan, definition);
            validateCommittedPlanningSelections(state, registry, playerId, plan.instanceId, definition, selections);
            return true;
        }
        return revalidatePlannedTargets(state, registry, playerId, definition, plan).valid;
    }
    catch {
        return false;
    }
}
function validatePlanCard(state, actor, action, registry) {
    const gate = planningGate(state, actor);
    if (gate !== null)
        return gate;
    // Submitting PlanCard again for a card in this actor's still-unlocked plan is
    // an atomic replacement. Validate the replacement against the state that
    // would exist after removing only that old plan; other staged cards keep
    // reserving their targets and contributing to the committed cost.
    const replacing = action.kind === 'PlanCard'
        && actor.staged.some((plan) => plan.instanceId === action.instanceId);
    const planningState = replacing ? {
        ...state,
        players: {
            ...state.players,
            [actor.id]: {
                ...actor,
                staged: actor.staged.filter((plan) => plan.instanceId !== action.instanceId),
            },
        },
    } : state;
    const planningActor = planningState.players[actor.id];
    const found = validateHandCard(planningState, planningActor, action.instanceId, registry);
    if ('ok' in found)
        return found;
    const { definition } = found;
    if (action.kind === 'PlanCard' && action.selections !== undefined && (action.cellId !== undefined || action.mode !== undefined
        || action.targetInstanceId !== undefined || action.targetCellId !== undefined))
        return rejected('PlanCard не может смешивать selections и legacy scalar targets');
    let genericSelections = action.kind === 'PlanCard'
        ? legacyPlanCardSelections(action, definition)
        : [{ stepId: 'placement', selection: [{ kind: 'cell', cellId: action.cellId }] }];
    try {
        genericSelections = validateCommittedPlanningSelections(planningState, registry, planningActor.id, action.instanceId, definition, genericSelections).selections;
    }
    catch (error) {
        return rejected(`цель или режим карты недопустимы: ${error instanceof Error ? error.message : String(error)}`);
    }
    if (definition.type === 'UNIT' || definition.type === 'STRUCTURE') {
        const destination = validateFieldDestination(planningState, planningActor, placementCellFromSelections(genericSelections), definition, genericSelections, registry);
        if (!destination.ok)
            return destination;
    }
    else if ((definition.type === 'SPELL' || definition.type === 'VIRUS') && action.kind === 'PlanCard') {
        if (action.cellId !== undefined)
            return rejected('SPELL не занимает ячейку поля');
        const selectedTarget = targetInstanceFromSelections(genericSelections);
        if (definition.type === 'VIRUS' && selectedTarget === undefined) {
            return rejected('VIRUS требует targetInstanceId');
        }
        if (definition.planning?.uniqueTargetPerRound === true && selectedTarget !== undefined) {
            const duplicate = planningActor.staged.some((plan) => {
                if (plan.kind !== 'SPELL' && plan.kind !== 'VIRUS')
                    return false;
                if (targetInstanceFromSelections(planSelections(plan)) !== selectedTarget)
                    return false;
                return planningState.instances[plan.instanceId]?.defId === definition.id;
            });
            if (duplicate)
                return rejected('эту карту нельзя повторно выбрать той же целью в одном раунде');
        }
    }
    else if (definition.type === 'REACTION' && action.kind === 'PlanCard') {
        if (genericSelections.length !== 0)
            return rejected('REACTION не принимает targets');
        const duplicate = planningActor.armedReactions.some((id) => planningState.instances[id]?.defId === definition.id)
            || planningActor.staged.some((plan) => planningState.instances[plan.instanceId]?.defId === definition.id);
        if (duplicate)
            return rejected('нельзя зарядить две Реакции одного defId');
    }
    else {
        return rejected(`тип ${definition.type} ещё нельзя добавлять в universal-plan`);
    }
    const committed = committedPlanCost(planningState, planningActor, registry);
    if (committed === undefined)
        return rejected('текущий план ссылается на неизвестную карту');
    const cost = effectiveCardCost(planningState, registry, action.instanceId);
    const available = availableEnergy(planningState, planningActor);
    if (committed + cost > available) {
        return rejected(`недостаточно энергии: план стоит ${committed + cost}, доступно ${available}`);
    }
    return accepted();
}
/** Чистый авторитетный гейт Action. */
export function validate(state, action, by, registry) {
    if (state.status.phase !== 'active')
        return rejected('матч уже завершён');
    if (!Object.hasOwn(state.players, by))
        return rejected(`неизвестный игрок "${by}"`);
    const actor = state.players[by];
    if (action.playerId !== by)
        return rejected(`игрок "${by}" не может действовать от имени "${action.playerId}"`);
    switch (action.kind) {
        case 'PlanCard':
        case 'PlaceUnit':
            return validatePlanCard(state, actor, action, registry);
        case 'UnplanCard': {
            const gate = planningGate(state, actor);
            if (gate !== null)
                return gate;
            return actor.staged.some((plan) => plan.instanceId === action.instanceId)
                ? accepted()
                : rejected(`карта "${action.instanceId}" отсутствует в плане этого игрока`);
        }
        case 'Unplace': {
            const gate = planningGate(state, actor);
            if (gate !== null)
                return gate;
            return actor.staged.some((plan) => isFieldPlan(plan) && plan.cellId === action.cellId)
                ? accepted()
                : rejected(`в ячейке "${action.cellId}" нет выкладки этого игрока`);
        }
        case 'Ready': {
            const gate = planningGate(state, actor);
            return gate ?? accepted();
        }
        case 'Resolve': {
            const pending = state.pendingChoice;
            if (pending === undefined)
                return rejected('матч не ожидает выбора');
            if (pending.playerId !== by)
                return rejected(`выбор должен сделать игрок "${pending.playerId}"`);
            if (pending.choiceId !== action.choiceId)
                return rejected(`choiceId "${action.choiceId}" не ожидается`);
            if ('tokens' in action.selection) {
                const tokens = action.selection.tokens;
                if (!Array.isArray(tokens) || tokens.some((token) => typeof token !== 'string' || token === '')) {
                    return rejected('selection.tokens должен быть массивом непустых choiceToken');
                }
                if (new Set(tokens).size !== tokens.length)
                    return rejected('повтор choiceToken запрещён');
                const cardinality = pending.cardinality;
                const candidates = pending.candidates;
                if (cardinality === undefined || candidates === undefined)
                    return rejected('pendingChoice не содержит A3 descriptor');
                if (tokens.length < cardinality.min || tokens.length > cardinality.max) {
                    return rejected(`выбрано ${tokens.length} целей, допустимо ${cardinality.min}..${cardinality.max}`);
                }
                const available = new Set(candidates.map((candidate) => candidate.choiceToken));
                if (tokens.some((token) => !available.has(token)))
                    return rejected('choiceToken отсутствует среди кандидатов');
                return accepted();
            }
            const picked = action.selection?.picked;
            if (!Array.isArray(picked))
                return rejected('selection.picked должен быть массивом');
            if (picked.some((id) => typeof id !== 'string' || id === ''))
                return rejected('selection.picked должен содержать непустые instanceId');
            const request = pending.request;
            if (request === undefined)
                return rejected('legacy Resolve несовместим с A3 pendingChoice');
            if (typeof request !== 'object' || request === null || typeof request.kind !== 'string' || request.kind === '') {
                return rejected('pendingChoice содержит некорректный request');
            }
            if (!Number.isInteger(request.count) || request.count < 0)
                return rejected('pendingChoice содержит некорректный count');
            if (picked.length !== request.count)
                return rejected(`выбрано ${picked.length} целей, требуется ${request.count}`);
            if (request.candidates !== undefined) {
                if (!Array.isArray(request.candidates))
                    return rejected('pendingChoice содержит некорректный candidates');
                for (const id of picked)
                    if (!request.candidates.includes(id))
                        return rejected(`цель "${id}" отсутствует среди кандидатов`);
            }
            return accepted();
        }
    }
}
//# sourceMappingURL=validate.js.map